import isEqual from 'react-fast-compare';

import {ArchiveStatus, NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import {DEFAULT_PAGE_SIZE} from 'nav/sidepanel/PageSizeUtil';
import {Thunk} from 'redux-stores/Store';
import {searchAssayRuns, legacySearchAssayRuns} from 'results/assays/AssayRecordActions';
import {
  Filter,
  FilterKey,
  keyToEmptyFilter,
  MultiValueFilter,
  SearchParams,
  SingleValueFilter,
} from 'search/SearchConstants';
import {Optional} from 'util/Types';

import {AvailabilityStatus, BenchlingBlobsByApiId} from './RunsInboxModalConstants';

function assayRunSearchParams(schemaId: string, filters: Array<Filter>, offset: number = 0): SearchParams {
  return {
    offset,
    limit: DEFAULT_PAGE_SIZE,
    query: '',
    filters: [
      ...filters,
      {
        ...keyToEmptyFilter(FilterKey.SCHEMA),
        value: [schemaId],
      },
    ],
    sorts: [],
    groupBy: null,
  };
}

export function fetchAssayRuns(schemaId: string, filters: Array<Filter>, offset?: number): Thunk<void> {
  const statusFilter = filters.find(
    (filter) => filter.key === FilterKey.RUN_AVAILABILITY
  ) as SingleValueFilter;
  const archiveFilter = filters.find(
    (filter) => filter.key === FilterKey.ARCHIVE_PURPOSES
  ) as MultiValueFilter;

  return searchAssayRuns({
    searchParams: assayRunSearchParams(schemaId, filters, offset),
    level:
      statusFilter.value === AvailabilityStatus.AVAILABLE &&
      isEqual(archiveFilter.value, [NOT_ARCHIVED_PURPOSE_STRING])
        ? 'write'
        : 'read',
  });
}

export function fetchMoreAssayRuns(schemaId: string, filters: Array<Filter>): Thunk<void> {
  return (dispatch, getState) => {
    const {offset} = getState().ui.results.runsInboxModal;
    dispatch(fetchAssayRuns(schemaId, filters, offset));
  };
}

// TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
export function legacyFetchAssayRuns(
  schemaId: string,
  searchFilter: string,
  statusFilter: AvailabilityStatus,
  archiveFilter: ArchiveStatus,
  nextToken?: Optional<string>
): Thunk<void> {
  return legacySearchAssayRuns({
    schemaId,
    nextToken,
    level:
      statusFilter === AvailabilityStatus.AVAILABLE && archiveFilter === ArchiveStatus.NOT_ARCHIVED
        ? 'write'
        : 'read',
    query: searchFilter,
    isAvailable: statusFilter === AvailabilityStatus.AVAILABLE,
    isArchived: archiveFilter === ArchiveStatus.IS_ARCHIVED,
  });
}

// TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
export function legacyFetchMoreAssayRuns(
  schemaId: string,
  searchFilter: string,
  statusFilter: AvailabilityStatus,
  archiveFilter: ArchiveStatus
): Thunk<void> {
  return (dispatch, getState) => {
    const {nextToken} = getState().ui.results.runsInboxModal;
    dispatch(legacyFetchAssayRuns(schemaId, searchFilter, statusFilter, archiveFilter, nextToken));
  };
}

export enum RunsInboxModalActionType {
  BLOB_SEARCH_COMPLETED = 'BLOB_SEARCH_COMPLETED',
  CHANGE_FILTERS = 'CHANGE_FILTERS',
  RESET_STATE = 'RUNS_INBOX_RESET_STATE',
  SELECT_RUN = 'RUNS_INBOX_SELECT_RUN',
  CHANGE_SEARCH_FILTER = 'RUNS_INBOX_CHANGE_SEARCH_FILTER', // TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
  CHANGE_STATUS_FILTER = 'RUNS_INBOX_CHANGE_STATUS_FILTER ', // TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
  CHANGE_ARCHIVE_FILTER = 'RUNS_INBOX_CHANGE_ARCHIVE_FILTER', // TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
}
export interface BlobSearchCompletedAction {
  type: RunsInboxModalActionType.BLOB_SEARCH_COMPLETED;
  blobsByApiId: BenchlingBlobsByApiId;
}
export function blobSearchCompleted(blobsByApiId: BenchlingBlobsByApiId): BlobSearchCompletedAction {
  return {
    type: RunsInboxModalActionType.BLOB_SEARCH_COMPLETED,
    blobsByApiId,
  };
}

export interface ChangeFiltersAction {
  type: RunsInboxModalActionType.CHANGE_FILTERS;
  filters: Array<Filter>;
}
export function changeFilters(filters: Array<Filter>): ChangeFiltersAction {
  return {
    type: RunsInboxModalActionType.CHANGE_FILTERS,
    filters,
  };
}

export interface ResetStateAction {
  type: RunsInboxModalActionType.RESET_STATE;
}

export function resetState(): ResetStateAction {
  return {
    type: RunsInboxModalActionType.RESET_STATE,
  };
}

export interface SelectRunAction {
  type: RunsInboxModalActionType.SELECT_RUN;
  runId: string;
}

export function selectRun(runId: string): SelectRunAction {
  return {
    type: RunsInboxModalActionType.SELECT_RUN,
    runId,
  };
}

// TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
export interface ChangeSearchFilterAction {
  type: RunsInboxModalActionType.CHANGE_SEARCH_FILTER;
  searchFilter: string;
}
export function changeSearchFilter(searchFilter: string): ChangeSearchFilterAction {
  return {
    type: RunsInboxModalActionType.CHANGE_SEARCH_FILTER,
    searchFilter,
  };
}

// TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
export interface ChangeStatusFilterAction {
  type: RunsInboxModalActionType.CHANGE_STATUS_FILTER;
  statusFilter: AvailabilityStatus;
}
export function changeStatusFilter(statusFilter: AvailabilityStatus): ChangeStatusFilterAction {
  return {
    type: RunsInboxModalActionType.CHANGE_STATUS_FILTER,
    statusFilter,
  };
}

// TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
export interface ChangeArchiveFilterAction {
  type: RunsInboxModalActionType.CHANGE_ARCHIVE_FILTER;
  archiveFilter: ArchiveStatus;
}
export function changeArchiveFilter(archiveFilter: ArchiveStatus): ChangeArchiveFilterAction {
  return {
    type: RunsInboxModalActionType.CHANGE_ARCHIVE_FILTER,
    archiveFilter,
  };
}

export type RunsInboxModalAction =
  | BlobSearchCompletedAction
  | ChangeFiltersAction
  | ResetStateAction
  | SelectRunAction
  | ChangeSearchFilterAction
  | ChangeStatusFilterAction
  | ChangeArchiveFilterAction;
